import { BaseActionObject, StateNodeConfig, assign } from "xstate4"
import { getPlanHoursData, getRoundedMaxAvailableHours } from "../../../helpers"
import { Context, LoadableSchema, Transitions } from "../../types"
import { getMaxAvailableContentDurationInvoke } from "./transitionalStates/getMaxAvailableContentDuration"

export const startPlan: StateNodeConfig<
  Context,
  LoadableSchema,
  Transitions,
  BaseActionObject
> = {
  entry: assign<Context>(ctx => ({
    modifyHoursModal: true,
    seconds:
      ctx.seconds ||
      (ctx.studyPlan.subType === "ADAPTIVE"
        ? ctx.studyPlan.targetSeconds
        : ctx.studyPlan.totalSeconds)
  })),
  exit: assign<Context>({
    modifyHoursModal: false
    // Don't clean parent state here so will be able to return to it
  }),
  initial: "loading",
  states: {
    loading: { ...getMaxAvailableContentDurationInvoke },
    idle: {
      on: {
        "seconds.update": {
          actions: assign((ctx, { seconds }) => {
            const { maxAvailableContentDuration, program, subject } = ctx

            const { minHours } = getPlanHoursData(program, subject)
            const maxRoundedHours = getRoundedMaxAvailableHours(
              program,
              subject,
              maxAvailableContentDuration
            )

            return {
              seconds:
                seconds < minHours * 3600
                  ? minHours * 3600
                  : seconds > maxRoundedHours * 3600
                  ? maxRoundedHours * 3600
                  : seconds
            }
          })
        },
        BUILD_STUDY_PLAN: [
          {
            /* Update target seconds for draft plan */
            target: "#StudyPlan.build_populated_study_plan_transition",
            actions: assign<Context>(_ctx => ({
              isBlank: false
            })),
            cond: (ctx, _evt) => {
              const minAvailableSeconds =
                getPlanHoursData(ctx.program, ctx.subject).minHours * 3600
              const roundedUpMaxAvailableSeconds =
                getRoundedMaxAvailableHours(
                  ctx.program,
                  ctx.subject,
                  ctx.maxAvailableContentDuration
                ) * 3600

              return (
                ctx.seconds >= minAvailableSeconds &&
                ctx.seconds <= roundedUpMaxAvailableSeconds
              )
            }
          },
          {
            /*
          Plans cannot be created under 5 hours or above 30 hours
          Throw a error message
         */
            actions: assign({
              snackbar: (ctx, _evt) => ({
                message: `A study plan cannot be built under 5 hours or over 30 hours. Plan is current being build with ${
                  ctx.seconds / 3600
                } hours.`,
                withUndo: false,
                error: true
              })
            }),
            cond: (ctx, _evt) => {
              const minAvailableSeconds =
                getPlanHoursData(ctx.program, ctx.subject).minHours * 3600
              const roundedUpMaxAvailableSeconds =
                getRoundedMaxAvailableHours(
                  ctx.program,
                  ctx.subject,
                  ctx.maxAvailableContentDuration
                ) * 3600

              return (
                ctx.seconds < minAvailableSeconds ||
                ctx.seconds > roundedUpMaxAvailableSeconds
              )
            }
          }
        ],
        START_BLANK_STUDY_PLAN: {
          target: "#StudyPlan.build_study_plan_transition",
          actions: assign((ctx, _evt) => ({
            isBlank: true,
            seconds: ctx.seconds,
            previousParentState: "study_plans_overview"
          }))
        },
        CANCEL: [
          // NOTE: this is cancel before create a draft plan. No API cancel call required.
          {
            target: "#StudyPlan.study_plans_overview.loading",
            cond: ctx => ctx.previousParentState === "study_plans_overview",
            actions: assign((_ctx, _evt_) => ({
              isBlank: null,
              seconds: null,
              studyPlan: null
            }))
          },
          {
            target: "#StudyPlan.review_plan",
            cond: ctx => ctx.previousParentState === "review_plan",
            actions: assign((ctx, _evt_) => ({
              seconds: ctx.studyPlan
                ? ctx.studyPlan.targetSeconds
                : ctx.maxAvailableContentDuration
            }))
          },
          {
            target: "#StudyPlan.edit_plan",
            cond: ctx => ctx.previousParentState === "edit_plan",
            actions: assign((ctx, _evt_) => ({
              seconds: ctx.studyPlan
                ? ctx.studyPlan.targetSeconds
                : ctx.maxAvailableContentDuration
            }))
          }
        ]
      }
    }
  }
}
